import { CircularProgress, styled } from '@mui/material';

import { DeferredImage, Skeleton } from '@app-velocity/ui';
import { useI18n } from '@app-velocity/i18n';

import { useSingleAiResponseContext } from '../SingleAiResponse/hooks/useSingleAiResponseContext';
import { useAiChatContext } from '../../hooks/useAiChatContext';

const StyledHeader = styled('header')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  cursor: 'pointer',
});

const StyledMeta = styled('div')({
  fontSize: 11,
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  overflow: 'hidden',
});

const StyledImage = styled('div')(({ theme }) => ({
  backgroundColor: '#E3E5EA',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 65,
  height: 40,
  boxSizing: 'border-box',
  padding: theme.spacing(1),
  borderRadius: theme.spacing(1),
  img: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    borderRadius: theme.spacing(0.75),
  },
}));

const StyledPageName = styled('strong')({
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
});

const StyledAnalysing = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  color: 'var(--text-text-secondary-color)',
  fontSize: 13,
  fontWeight: 400,
}));

const StyledCircularProgress = styled(CircularProgress)({
  color: 'var(--icon-icon-tertiary-color)',
});

export const AiReviewHeader = () => {
  const { message, page, partials, stepNumber, analysing } =
    useSingleAiResponseContext();

  const { navigateToElement } = useAiChatContext();

  const { t } = useI18n();

  const handleNavigateToElement = () => {
    if (!page?.fileName || !navigateToElement || !message || !partials) return;

    const { overlayIds } = message;

    if (!overlayIds || overlayIds.length === 0) {
      navigateToElement(page?.fileName);

      return;
    }

    const lastOverlay = overlayIds[overlayIds.length - 1];
    const fullOverlay = partials.find((partial) => partial.id === lastOverlay);

    if (!fullOverlay || !fullOverlay.fileName) {
      navigateToElement(page?.fileName);

      return;
    }

    navigateToElement(fullOverlay.fileName);
  };

  return (
    <StyledHeader onClick={handleNavigateToElement}>
      {message?.media || (page && page?.image) ? (
        <StyledImage>
          <DeferredImage
            src={message?.media || page?.image || ''}
            alt={`Page ${page?.name}`}
          />
        </StyledImage>
      ) : (
        <Skeleton height={40} width={65} borderRadius={1} />
      )}
      {page ? (
        <StyledMeta>
          <StyledPageName>{page?.name}</StyledPageName>
          <span>
            {t('prototypes:step_n', {
              number: stepNumber,
            })}
          </span>
        </StyledMeta>
      ) : (
        <Skeleton
          height={28}
          width={100}
          borderRadius={1}
          margin="0 auto 0 0"
        />
      )}
      {analysing ? (
        <StyledAnalysing>
          {t('prototypes:analysing')}{' '}
          <StyledCircularProgress thickness={5} size={12} color="inherit" />
        </StyledAnalysing>
      ) : null}
    </StyledHeader>
  );
};
